<!--
  File name: DataBreachActivityLogs.vue
  Program Description:
    - This component displays the list of fields that have been modified.
-->
<template>
  <v-container>
    <template v-if="!loading">
      <v-row>
        <v-col cols="8">
          <h1>
            <b> {{ formItem.dbnNumber }} - Logs</b>
          </h1>
        </v-col>
        <v-col>
          <v-breadcrumbs
            large
            :items="crumbs.items"
            :divider="crumbs.divider"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="formItem.activityLogs || []"
            :items-per-page="-1"
            :expanded.sync="expanded"
            item-key="dateUpdated"
            show-expand
            disable-sort
            single-expand
            hide-default-footer
          >
            <template #top>
              <v-col style="border-bottom: thin solid rgba(0, 0, 0, 0.12)">
                <v-btn
                  text
                  color="grey darken-1"
                  @click="$router.push({
                    name: 'DataBreachView',
                    params: {
                      dataTab: 'pdbnf',
                      operation: 'view',
                      id: formItem._id
                    }
                  })"
                >
                  <v-icon>
                    mdi-arrow-left-thin
                  </v-icon>
                  Back
                </v-btn>
              </v-col>
            </template>
            <template v-slot:[`item.dateUpdated`]="{ item }">
              {{ dateAndTimeCreated(item.dateUpdated) }}
            </template>
            <template #expanded-item="{ item }">
              <td
                v-if="expanded.length && expanded[0].dateUpdated === item.dateUpdated"
                class="pa-5"
                :colspan="8"
              >
                <h2 class="mb-3 pl-4">
                  Updated Fields ({{ item.documentType }})
                </h2>
                <v-expansion-panels
                  v-model="panel"
                  accordion
                >
                  <v-expansion-panel
                    v-for="(field,i) in item.field"
                    :key="i"
                  >
                    <v-expansion-panel-header v-if="(field.oldValue || field.newValue) && (field.oldValue !== field.newValue)">
                      {{ (fields[field.fieldName] && fields[field.fieldName].name) || field.fieldName }}
                    </v-expansion-panel-header>
                    <template v-if="i === panel">
                      <v-expansion-panel-content>
                        <v-row v-if="field.fieldName === 'fileUpload'">
                          <v-col>
                            This field has been modified.
                          </v-col>
                        </v-row>
                        <v-row v-else>
                          <v-col cols="5">
                            <ActivityLogsInput
                              :field-name="field.fieldName"
                              :component="fields[field.fieldName].is"
                              :label="fields[field.fieldName].name"
                              :value="field.oldValue"
                              :new-value="field.newValue"
                            />
                          </v-col>
                          <v-col
                            cols="2"
                            class="d-flex justify-center px-0"
                          >
                            <v-icon color="primary">
                              mdi-arrow-right-bold
                            </v-icon>
                          </v-col>
                          <v-col cols="5">
                            <ActivityLogsInput
                              :field-name="field.fieldName"
                              :component="fields[field.fieldName].is"
                              :label="fields[field.fieldName].name"
                              :value="field.newValue"
                            />
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </template>
                  </v-expansion-panel>
                </v-expansion-panels>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <v-row>
        <v-col>
          <v-skeleton-loader
            class="mx-auto"
            type="text"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-skeleton-loader
            class="mx-auto"
            type="table"
          />
        </v-col>
      </v-row>
    </template>
    <v-btn
      v-if="showFab"
      fab
      fixed
      bottom
      right
      class="primary"
      @click="scrollToTop()"
    >
      <v-icon
        color="#FFFFFF"
      >
        mdi-arrow-up
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import crud from '@/utils/rest/crud'
import implem from '@/assets/implem.png'
import fields from '@/meta/activityLogs'
import { dateAndTimeCreated } from '@/utils/helpers'
import ActivityLogsInput from './ActivityLogsInput.vue'

export default {
  name: 'DataBreachActivityLogs',
  components: {
    ActivityLogsInput
  },
  data: () => ({
    formItem: {},
    panel: false,
    expanded: [],
    loading: false,
    showFab: false
  }),
  computed: {
    pdbnfCrud () {
      return crud('pdbnf')
    },
    implem () {
      return implem
    },
    fields () {
      return fields
    },
    dateAndTimeCreated () {
      return date => dateAndTimeCreated(date)
    },
    headers () {
      return [
        {
          text: 'Document Type',
          value: 'documentType'
        },
        {
          text: 'Date',
          value: 'dateUpdated'
        },
        {
          text: 'Updated by',
          value: 'updatedBy'
        }
      ]
    },
    crumbs () {
      return {
        divider: '>',
        items: [
          {
            text: 'PDBNF',
            disabled: false,
            exact: true,
            to: {
              name: 'DataBreachView',
              params: {
                dataTab: 'pdbnf',
                operation: 'view',
                id: this.formItem._id
              }
            }

          },
          {
            text: `${this.formItem.dbnNumber} - Logs`,
            disabled: true
          }
        ]
      }
    }

  },
  watch: {
    expanded: function () { this.panel = false }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
  },
  async created () {
    this.loading = true
    await this.getPdbnf()
    this.loading = false
  },
  methods: {
    // Gets the selected PDBNF Data
    async getPdbnf () {
      const payload = { advancedQuery: { _id: { $eq: this.$route.params.id } } }
      const resp = await this.pdbnfCrud.list(payload)
      this.formItem = resp.entries && resp.entries[0]
    },
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    onScroll () {
      this.showFab = window.top.scrollY > 200
    }
  }
}
</script>

<style scoped>
.v-breadcrumbs >>> a{
  color: #162b54 !important;
  font-weight: bolder;
}
.v-breadcrumbs{
  font-weight: bolder;
}
.transparent-bg {
  background-color: rgba(0,0,0,0);
}
</style>
